import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";

const FindItLocalList = ({ category }) => {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();

    const userId = authState?.user?._id;
    const token = authState?.token;

    const [records, setRecords] = useState([]);
    const [selected, setSelected] = useState(null);

    const hasFetchedRef = useRef(false);

    // ---------------- FILTER STATES ----------------
    const [search, setSearch] = useState("");
    const [cityFilter, setCityFilter] = useState("");
    const [tagFilter, setTagFilter] = useState("");

    const fetchList = async () => {
        const res = await fetchData({
            url: "/pd/finditlocal/list",
            method: "post",
            data: { category },
            headers: {
                Authorization: token
            }
        });

        if (res?.status) {
            setRecords(res.records || []);
        }
    };

    useEffect(() => {
        fetchList();
    }, [category]);

    // ---------------- FILTER LOGIC ----------------
    const filteredRecords = useMemo(() => {
        return (records || []).filter((item) => {
            const searchText = search.toLowerCase();

            const matchSearch =
                !searchText ||
                item.title?.toLowerCase().includes(searchText) ||
                item.quickJD?.toLowerCase().includes(searchText);

            const matchCity =
                !cityFilter ||
                item.city?.toLowerCase().includes(cityFilter.toLowerCase());

            const tagArray = tagFilter
                .split(",")
                .map(t => t.trim().toLowerCase())
                .filter(Boolean);

            const matchTags =
                tagArray.length === 0 ||
                tagArray.some(tag =>
                    (item.tags || [])
                        .map(t => t.toLowerCase())
                        .includes(tag)
                );

            return matchSearch && matchCity && matchTags;
        });
    }, [records, search, cityFilter, tagFilter]);

    const clearFilters = () => {
        setSearch("");
        setCityFilter("");
        setTagFilter("");
    };

    const handleCall = (item) => {
        const number = item?.mobileNumber;
        if (!number) {
            // console.log("DEBUG ITEM:", item); // IMPORTANT for future debugging
            alert("📵 No phone number available");
            return;
        }
        if (!isCallAllowed(item)) {
            alert("📵 Calls allowed only during specified days/timing");
            return;
        }
        window.location.href = `tel:${number}`;
    };

    const handleWhatsApp = (item) => {
        const number = item.whatsappNumber || item.mobileNumber;
        if (!number) {
            alert("💬 No WhatsApp number available");
            return;
        }
        const cleanNumber = String(number).replace(/[^\d]/g, "");
        const message = encodeURIComponent(
            `Hi ${item.contactPerson || item.postedByName || "there"},\n\n` +
            `I saw your listing on Think5Whys.\n\n` +
            `👉 Title: ${item.title}\n` +
            `📍 Location: ${item.area || "Not specified"}\n\n` +
            `📍 Location: ${item.city || "Not specified"}\n\n` +
            `I am interested. Please contact..\n\nThanks!`
        );
        window.open(`https://wa.me/${cleanNumber}?text=${message}`, "_blank");
    };

    const handleEmail = (item) => {
        const email = item.emailId;

        if (!email) {
            alert("✉ No email available");
            return;
        }

        window.location.href = `mailto:${email}`;
    };

    // ---------------- ACTION HELPERS ----------------
    const getCleanNumber = (num) => {
        if (!num) return null;
        return String(num).replace(/[^\d]/g, "");
    };

    const isCallAllowed = (item) => {
        if (!item.callDays && !item.callTiming) return true;

        const now = new Date();

        const currentDay = now
            .toLocaleDateString("en-US", { weekday: "long" })
            .toLowerCase();

        const days = item.callDays
            ?.toLowerCase()
            .split(",")
            .map(d => d.trim()) || [];

        if (days.length && !days.includes(currentDay)) return false;

        if (item.callTiming?.includes("-")) {
            const [start, end] = item.callTiming.split("-");

            const toHours = (t) => {
                const [h, m] = t.trim().split(":").map(Number);
                return h + (m || 0) / 60;
            };

            const current = now.getHours() + now.getMinutes() / 60;

            return current >= toHours(start) && current <= toHours(end);
        }

        return true;
    };

    return (
        <div className="space-y-3 pt-3">
            <div className="bg-[#0f172a] border border-gray-600 rounded-xl p-3 space-y-2">
                <div className="text-xs text-cyan-300 font-bold">
                    🔎 Filter FoundIt To Get Necessary
                </div>

                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search title or description..."
                    className="w-full bg-gray-900 text-white p-2 rounded text-xs"
                />

                <div className="grid grid-cols-2 gap-2">
                    <input
                        value={cityFilter}
                        onChange={(e) => setCityFilter(e.target.value)}
                        placeholder="City (e.g. Mumbai)"
                        className="bg-gray-900 text-white p-2 rounded text-xs"
                    />

                    <input
                        value={tagFilter}
                        onChange={(e) => setTagFilter(e.target.value)}
                        placeholder="Tags (comma separated)"
                        className="bg-gray-900 text-white p-2 rounded text-xs"
                    />
                </div>

                <button
                    onClick={clearFilters}
                    className="text-xs px-2 py-1 w-fit text-cyan-300 underline"
                >
                    Clear Filters
                </button>
            </div>

            {/* ---------------- LIST ---------------- */}
            <div className="bg-[#111827] rounded-xl border border-gray-800">
                <div className="px-3 py-2 text-xs text-cyan-300 font-bold border-b border-gray-700">
                    ⚡ Hustle Opportunities ({filteredRecords.length})
                </div>

                {filteredRecords.map((item) => (
                    <div key={item._id} className="border-t border-gray-800 p-3">

                        <div
                            onClick={() => setSelected(item)}
                            className="cursor-pointer flex justify-between items-center"
                        >
                            <div>
                                <div className="text-xs font-semibold text-white">
                                    {item.title}
                                </div>

                                <div className="text-[10px] text-gray-200">
                                    📍 {item.city || "Remote"}
                                </div>
                            </div>

                            <div className="text-[10px] text-yellow-300">
                                Expires:{" "}
                                {new Date(item.dateExpires).toLocaleDateString("en-IN")}
                            </div>
                        </div>

                        {selected?._id === item._id && (
                            <div className="mt-3 bg-[#0f172a] p-4 rounded-lg border border-cyan-500/20 space-y-3">
                                {/* TITLE */}
                                <div className="text-sm font-bold text-white">
                                    {item.title}
                                </div>
                                <div className="text-sm font-bold text-white">
                                    {item.briefDesc}
                                </div>
                                {/* LOCATION */}
                                <div className="text-[10px] text-gray-200">
                                    📍 {item.area}, {item.city}
                                </div>

                                {/* TAGS */}
                                <div className="flex flex-wrap gap-2">
                                    {item.tags?.map(tag => (
                                        <span
                                            key={tag}
                                            className="bg-gray-700 px-2 py-1 rounded text-[10px]"
                                        >
                                            #{tag}
                                        </span>
                                    ))}
                                </div>

                                <div className="text-xs text-purple-300">
                                    ⚡ {item.streakOffer}
                                </div>

                                <div className="text-xs text-cyan-300">
                                    {item.vibe}
                                </div>

                                {/* CONTACT ACTIONS */}
                                <div className="flex flex-row gap-2">

                                    <button
                                        onClick={() => handleCall(item)}
                                        disabled={!isCallAllowed(item)}
                                        className={`text-[10px] lg:text-xs w-fit px-2 py-1 rounded-md font-bold transition-all
                                            ${isCallAllowed(item)
                                                ? "bg-green-500 text-black"
                                                : "bg-gray-600 text-gray-100 cursor-not-allowed"
                                            }`}
                                    >
                                        📞 Call
                                    </button>

                                    <button
                                        onClick={() => handleWhatsApp(item)}
                                        className="text-[10px] lg:text-xs w-fit px-2 py-1 rounded-md bg-green-600 text-white font-bold"
                                    >
                                        💬 WhatsApp
                                    </button>

                                    <button
                                        onClick={() => handleEmail(item)}
                                        className="text-[10px] lg:text-xs w-fit px-2 py-1 rounded-md bg-blue-600 text-white font-bold"
                                    >
                                        ✉ Email
                                    </button>

                                </div>

                                {/* AVAILABILITY */}
                                <div className="text-[10px] text-gray-200">
                                    ⏰ Available: {item.callDays || "All Days"} | {item.callTiming || "24x7"}
                                </div>

                                {/* POSTED BY */}
                                <div className="text-[10px] text-gray-200">
                                    👤 Posted by: <span className="text-white">{item.postedByName}</span>
                                </div>

                                {/* EXPIRY */}
                                <div className="text-[10px] text-yellow-300">
                                    ⏳ Expires: {new Date(item.dateExpires).toLocaleDateString("en-IN")}
                                </div>

                                {/* VIEWS */}
                                <div className="text-[10px] text-cyan-400 font-semibold">
                                    👀 {item.viewCount || 0} Views
                                </div>

                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FindItLocalList;